/**
 * AdminMesaiMolaTakip.jsx
 *
 * Mesai & Mola Takibi — gunun tum danismanlari icin mesai baslangici ve
 * mola ozeti. Route: /danisman/mesai-mola
 * Erisim: danisman, admin, superadmin
 *
 * Recep'in istegi (25 Agustos 2026): "danisman ne kadar molada kalmis,
 * mesaiye kacta baslamis onun takibinin oldugu bir sayfa".
 *
 * Backend: GET /danisman/cockpit/mesai-mola-raporu?tarih=YYYY-MM-DD
 *   (routes/danisman_cockpit.py::cockpit_mesai_mola_raporu)
 *   Veri ZATEN toplaniyordu (danisman_molalar / danisman_mesai); bu tur
 *   yalnizca okuma + ekran ekledi, yeni bir yazma yolu ACMADI.
 *
 * ROL IZOLASYONU — bilincli istisna: bu ekran danisman rolunde de TUM
 * ekibi gosterir. Donen veri para/lead degil saat damgasi; ekip
 * seffafligi icin istendi (bkz. backend docstring'i ve
 * cockpit_mesai_listesi'ndeki ayni not). Bu yuzden burada `isAdmin`
 * guard'i YOKTUR — eksik sanip eklemeyin.
 *
 * MESAI BITISI: su an hicbir yerde yazilmiyor (`mesai-bitir` ucu YOK), bu
 * yuzden "Bitis" kolonu pratikte hep "—" gosterir. Kolon bilerek duruyor:
 * bitis yazilmaya baslandiginda ekran degismeden calisir. Ekranda bunu
 * soyleyen bir dipnot var — aksi halde "veri kayboluyor" sanilir.
 *
 * SU AN MOLADA: `bitis` bos olan mola HALA DEVAM EDIYOR demektir; suresi
 * "simdi"ye kadar sayilir. Bu satirlar rozetle isaretlenir, cunku 12
 * dakikalik bir sayi "bitmis mola" ile "devam eden mola" arasinda ayrim
 * yapmaz.
 */
import { useState, useCallback, useEffect } from 'react';
import apiClient from '../services/apiClient';
import AdminLayout from '../components/admin/AdminLayout';
import PageHeader from '../components/admin/PageHeader';
import ErrorBoundary, { BlockErrorFallback } from '../components/ErrorBoundary';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { rootBreadcrumb } from '../utils/adminBreadcrumbs';
import { Skeleton } from '../components/ui/skeleton';
import { ErrorRetryCrm, INPUT_CLS, LBL_CLS } from '../components/crm/DanismanCockpitShared';
import { trNowLocalDateInput } from '../utils/trDateTime';
import { Coffee, Clock, Users } from 'lucide-react';

/** ISO damgasindan TR duvar saati (SS:DD). Bos/bozuk deger "—". */
function saatTR(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  // Sabit +3 offset — backend'in services/tr_time.py yaklasimiyla ayni,
  // tarayicinin yerel saat dilimine BAGIMLI DEGIL.
  const tr = new Date(d.getTime() + 3 * 60 * 60 * 1000);
  return `${String(tr.getUTCHours()).padStart(2, '0')}:${String(tr.getUTCMinutes()).padStart(2, '0')}`;
}

/** Dakikayi "1s 25dk" bicimine cevirir. 0 icin "—" DEGIL "0 dk" — molasi
 *  olmayan danisman ile verisi olmayan danismani ayirt etmek gerekir. */
function sureTR(dk, tr) {
  const n = Number(dk) || 0;
  if (n < 60) return `${n} ${tr('dk', 'min')}`;
  const s = Math.floor(n / 60);
  const k = n % 60;
  return k === 0 ? `${s} ${tr('sa', 'h')}` : `${s} ${tr('sa', 'h')} ${k} ${tr('dk', 'min')}`;
}

export default function AdminMesaiMolaTakip() {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const tr = (t, e) => (lang === 'en' ? e : t);

  const [tarih, setTarih] = useState(trNowLocalDateInput());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hata, setHata] = useState(false);

  const fetchRapor = useCallback(async () => {
    setLoading(true);
    setHata(false);
    try {
      const r = await apiClient.get(`/danisman/cockpit/mesai-mola-raporu?tarih=${tarih}`);
      setData(r.data);
    } catch {
      setHata(true);
    } finally {
      setLoading(false);
    }
  }, [tarih]);

  useEffect(() => { fetchRapor(); }, [fetchRapor]);

  const items = data?.items || [];
  const moladaSayisi = items.filter((r) => r.su_an_molada).length;
  const toplamMolaDk = items.reduce((acc, r) => acc + (Number(r.toplam_mola_dk) || 0), 0);

  return (
    <AdminLayout>
      <div className="-m-4 lg:-m-6 p-4 lg:p-6 min-h-full" style={{ backgroundColor: 'var(--crm-cream)' }}>
        <PageHeader
          breadcrumbs={[
            rootBreadcrumb(user?.role, tr),
            { label: tr('Mesai & Mola Takibi', 'Shift & Break Tracking') },
          ]}
          title={tr('Mesai & Mola Takibi', 'Shift & Break Tracking')}
          subtitle={tr(
            'Danışmanların mesaiye başlama saati ve gün içindeki mola süreleri.',
            'Consultant shift start times and break durations for the day.'
          )}
        />

        <ErrorBoundary fallback={(_e, retry) => <BlockErrorFallback onRetry={retry} tr={tr} />}>
          <div className="bg-white rounded-2xl p-4 sm:p-5" style={{ border: '1px solid var(--crm-card-border)' }}>
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div>
                <label htmlFor="mesai-tarih" className={LBL_CLS}>{tr('Tarih', 'Date')}</label>
                <input
                  id="mesai-tarih"
                  type="date"
                  value={tarih}
                  onChange={(e) => setTarih(e.target.value)}
                  className={INPUT_CLS}
                />
              </div>
              <div className="flex gap-3 ml-auto text-xs">
                <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gray-50 font-bold" style={{ color: 'var(--crm-navy-text)' }}>
                  <Users size={14} /> {items.length} {tr('danışman', 'consultants')}
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-50 text-amber-800 font-bold">
                  <Coffee size={14} /> {moladaSayisi} {tr('şu an molada', 'on break now')}
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gray-50 font-bold" style={{ color: 'var(--crm-navy-text)' }}>
                  <Clock size={14} /> {sureTR(toplamMolaDk, tr)} {tr('toplam', 'total')}
                </span>
              </div>
            </div>

            {loading && (
              <div className="space-y-2">
                {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
              </div>
            )}

            {!loading && hata && <ErrorRetryCrm onRetry={fetchRapor} tr={tr} />}

            {!loading && !hata && items.length === 0 && (
              <div className="border-2 border-dashed border-[var(--border-light)] rounded-xl p-6 text-center text-sm text-gray-400">
                {tr(
                  'Bu tarihte mesai veya mola kaydı yok.',
                  'No shift or break records for this date.'
                )}
              </div>
            )}

            {!loading && !hata && items.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b-2 border-[var(--border-light)]">
                    <tr style={{ color: 'var(--crm-muted)' }}>
                      <th scope="col" className="text-left pb-1.5 font-bold text-[11px] uppercase">{tr('Danışman', 'Consultant')}</th>
                      <th scope="col" className="text-left pb-1.5 font-bold text-[11px] uppercase">{tr('Mesai Başlangıcı', 'Shift Start')}</th>
                      <th scope="col" className="text-left pb-1.5 font-bold text-[11px] uppercase">{tr('Bitiş', 'End')}</th>
                      <th scope="col" className="text-right pb-1.5 font-bold text-[11px] uppercase">{tr('Mola Adedi', 'Breaks')}</th>
                      <th scope="col" className="text-right pb-1.5 pr-6 font-bold text-[11px] uppercase">{tr('Toplam Mola', 'Total Break')}</th>
                      <th scope="col" className="text-left pb-1.5 font-bold text-[11px] uppercase w-40">{tr('Durum', 'Status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((r) => (
                      <tr
                        key={r.user_id}
                        className="border-t"
                        style={{
                          borderColor: 'var(--crm-card-border)',
                          backgroundColor: r.user_id === user?.user_id ? 'rgba(46,107,143,0.08)' : undefined,
                        }}
                      >
                        <td className="py-2 font-semibold" style={{ color: 'var(--crm-navy-text)' }}>
                          {r.name || r.user_id}
                        </td>
                        <td className="py-2">{saatTR(r.mesai_baslangic)}</td>
                        <td className="py-2" style={{ color: 'var(--crm-muted)' }}>{saatTR(r.mesai_bitis)}</td>
                        <td className="py-2 text-right">{r.mola_adedi}</td>
                        <td className="py-2 pr-6 text-right font-bold" style={{ color: 'var(--crm-navy-text)' }}>
                          {sureTR(r.toplam_mola_dk, tr)}
                        </td>
                        <td className="py-2">
                          {r.su_an_molada ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-amber-100 text-amber-800 text-[11px] font-bold">
                              <Coffee size={11} />
                              {tr('Molada', 'On break')} · {saatTR(r.aktif_mola_baslangic)}
                            </span>
                          ) : (
                            <span className="text-[11px]" style={{ color: 'var(--crm-muted)' }}>
                              {tr('Çalışıyor', 'Working')}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* "Bitis" kolonunun neden bos oldugunu SOYLE. Aksi halde veri
                kayboluyor sanilir ve bug olarak raporlanir. */}
            <p className="text-[11px] mt-3" style={{ color: 'var(--crm-muted)' }}>
              {tr(
                'Not: Mesai bitişi henüz hiçbir yerde kaydedilmiyor, bu yüzden "Bitiş" sütunu boş görünür. Devam eden molalar şu ana kadar sayılır.',
                'Note: Shift end is not recorded anywhere yet, so the "End" column stays empty. Ongoing breaks are counted up to now.'
              )}
            </p>
          </div>
        </ErrorBoundary>
      </div>
    </AdminLayout>
  );
}
